// Cache utility for chat application
class ChatCache {
  constructor() {
    this.cache = new Map();
    this.maxSize = 100; // Maximum number of cached items
    this.maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.cleanupInterval = 60 * 60 * 1000; // Cleanup every hour
    
    // Start cleanup interval
    setInterval(() => this.cleanup(), this.cleanupInterval);
    
    // Load cached data from localStorage on initialization
    this.loadFromStorage();
  }

  // Generate cache key
  generateKey(type, id) {
    return `${type}_${id}`;
  }

  // Set cache item with metadata
  set(type, id, data, options = {}) {
    const key = this.generateKey(type, id);
    const item = {
      data,
      timestamp: Date.now(),
      type,
      id,
      size: this.calculateSize(data),
      ...options
    };

    // Check if we need to make space
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }

    this.cache.set(key, item);
    this.saveToStorage();
    return item;
  }

  // Get cache item
  get(type, id) {
    const key = this.generateKey(type, id);
    const item = this.cache.get(key);

    if (!item) return null;

    // Check if item is expired
    if (Date.now() - item.timestamp > this.maxAge) {
      this.cache.delete(key);
      this.saveToStorage();
      return null;
    }

    // Update access time
    item.lastAccessed = Date.now();
    return item.data;
  }

  // Check if item exists and is valid
  has(type, id) {
    const key = this.generateKey(type, id);
    const item = this.cache.get(key);
    
    if (!item) return false;
    
    // Check if expired
    if (Date.now() - item.timestamp > this.maxAge) {
      this.cache.delete(key);
      this.saveToStorage();
      return false;
    }
    
    return true;
  }

  // Remove specific item
  remove(type, id) {
    const key = this.generateKey(type, id);
    const removed = this.cache.delete(key);
    if (removed) {
      this.saveToStorage();
    }
    return removed;
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.saveToStorage();
  }

  // Get cache statistics
  getStats() {
    const items = Array.from(this.cache.values());
    const totalSize = items.reduce((sum, item) => sum + item.size, 0);
    const byType = items.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + 1;
      return acc;
    }, {});

    return {
      totalItems: this.cache.size,
      totalSize,
      byType,
      maxSize: this.maxSize,
      maxAge: this.maxAge
    };
  }

  // Calculate approximate size of data
  calculateSize(data) {
    try {
      return JSON.stringify(data).length;
    } catch {
      return 0;
    }
  }

  // Evict oldest items when cache is full
  evictOldest() {
    const items = Array.from(this.cache.entries());
    items.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    
    // Remove 20% of oldest items
    const toRemove = Math.ceil(this.maxSize * 0.2);
    for (let i = 0; i < toRemove && i < items.length; i++) {
      this.cache.delete(items[i][0]);
    }
  }

  // Cleanup expired items
  cleanup() {
    const now = Date.now();
    let removed = 0;

    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > this.maxAge) {
        this.cache.delete(key);
        removed++;
      }
    }

    if (removed > 0) {
      this.saveToStorage();
      console.log(`Cache cleanup: removed ${removed} expired items`);
    }
  }

  // Save cache to localStorage
  saveToStorage() {
    try {
      const cacheData = Array.from(this.cache.entries());
      localStorage.setItem('chatCache', JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save cache to localStorage:', error);
    }
  }

  // Load cache from localStorage
  loadFromStorage() {
    try {
      const cacheData = localStorage.getItem('chatCache');
      if (cacheData) {
        const entries = JSON.parse(cacheData);
        this.cache = new Map(entries);
        console.log(`Loaded ${this.cache.size} items from cache`);
      }
    } catch (error) {
      console.warn('Failed to load cache from localStorage:', error);
      this.cache = new Map();
    }
  }

  // Cache messages for a conversation
  cacheMessages(conversationId, messages) {
    return this.set('messages', conversationId, messages, {
      priority: 'high',
      maxAge: 2 * 60 * 60 * 1000 // 2 hours for messages
    });
  }

  // Get cached messages
  getCachedMessages(conversationId) {
    return this.get('messages', conversationId);
  }

  // Cache user profile
  cacheUserProfile(userId, profile) {
    return this.set('userProfile', userId, profile, {
      priority: 'medium',
      maxAge: 24 * 60 * 60 * 1000 // 24 hours for profiles
    });
  }

  // Get cached user profile
  getCachedUserProfile(userId) {
    return this.get('userProfile', userId);
  }

  // Cache conversation list
  cacheConversationList(userId, conversations) {
    return this.set('conversationList', userId, conversations, {
      priority: 'high',
      maxAge: 30 * 60 * 1000 // 30 minutes for conversation list
    });
  }

  // Get cached conversation list
  getCachedConversationList(userId) {
    return this.get('conversationList', userId);
  }

  // Cache media file
  cacheMedia(mediaId, mediaData) {
    return this.set('media', mediaId, mediaData, {
      priority: 'low',
      maxAge: 7 * 24 * 60 * 60 * 1000 // 7 days for media
    });
  }

  // Get cached media
  getCachedMedia(mediaId) {
    return this.get('media', mediaId);
  }

  // Preload important data
  async preloadData(userId) {
    const promises = [];
    
    // Preload conversation list
    if (!this.has('conversationList', userId)) {
      promises.push(this.loadConversationList(userId));
    }
    
    // Preload recent messages for active conversations
    const conversationList = this.getCachedConversationList(userId);
    if (conversationList) {
      conversationList.slice(0, 5).forEach(conv => {
        if (!this.has('messages', conv.id)) {
          promises.push(this.loadRecentMessages(conv.id));
        }
      });
    }
    
    return Promise.all(promises);
  }

  // Load conversation list (placeholder - implement with your API)
  async loadConversationList(userId) {
    // This would call your actual API
    // For now, return empty array
    return [];
  }

  // Load recent messages (placeholder - implement with your API)
  async loadRecentMessages(conversationId) {
    // This would call your actual API
    // For now, return empty array
    return [];
  }
}

// Create singleton instance
const chatCache = new ChatCache();

export default chatCache; 